<script setup lang="ts">
import { computed } from 'vue';
import { simpleFormatAllowedDecorate } from '@shared/app/utils/simpleFormatAllowedDecorate';
import { useMenuImageUrl } from '@shared/app/composables/useMenuImageUrl';
import { useRecipeImageUrl } from '@shared/app/composables/useRecipeImageUrl';
import ChevronRight from 'vue-material-design-icons/ChevronRight.vue';
import type { MenuDetailResponse } from '~~/server/api/kiwi_user/menus/detail';

const props = defineProps<{
  menuData?: MenuDetailResponse | null;
}>();

const { createMenuMainImageUrl } = useMenuImageUrl();
const { createRecipeMainImageUrl } = useRecipeImageUrl();

const leadHtml = computed(() => {
  if (!props.menuData?.lead) {
    return '';
  }

  return simpleFormatAllowedDecorate(props.menuData.lead);
});

const mainImageUrl = computed(() => {
  // NOTE: 献立のmain_imageがあればそれを使い、なければレシピ自体のmain_imageを使う
  if (props.menuData?.main_image) {
    return createMenuMainImageUrl({
      menuId: props.menuData.id,
      filename: props.menuData.main_image,
      w: 640,
      h: 480,
    });
  }

  const firstRecipe = props.menuData?.recipes[0];

  if (firstRecipe?.main_image) {
    return createRecipeMainImageUrl({
      recipeId: firstRecipe.id,
      filename: firstRecipe.main_image,
      w: 640,
      h: 480,
    });
  }

  return undefined;
});

function createRecipeImageUrl(recipe: MenuDetailResponse['recipes'][number]) {
  if (!recipe.main_image) {
    return;
  }

  return createRecipeMainImageUrl({
    recipeId: recipe.id,
    filename: recipe.main_image,
    w: 300,
    h: 300,
  });
}

const pointHtml = computed(() => {
  if (!props.menuData?.point) {
    return '';
  }

  return simpleFormatAllowedDecorate(props.menuData.point);
});
</script>

<template>
  <div>
    <OrganismsRecipeSearchForm class="mb-5" />

    <div class="bg-white border border-b-2 border-cream-500 rounded p-4 mb-5">
      <div>
        <h1 class="text-2xl font-bold text-gray-500 mb-5">
          {{ menuData?.title }}
        </h1>

        <div class="flex max-md:flex-col gap-5 mb-8">
          <div class="md:w-[45%] shrink-0">
            <div class="mb-3">
              <img v-if="mainImageUrl" :src="mainImageUrl" />
              <AtomsPlaceholderImage
                v-else
                class="w-full object-cover aspect-[640/480] bg-white"
              />
            </div>

            <AtomsRecipeInfoStats
              :energy="menuData?.energy"
              :cooking-time="menuData?.cooking_time"
              :estimated-cost="menuData?.estimated_cost"
            />

            <AtomsPickedTagsSection
              v-if="menuData?.associated_picked_tags"
              class="mt-5"
              :tags="menuData.associated_picked_tags"
              title="この献立の特徴"
            />
          </div>

          <div>
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="sanitized-html text-lg text-gray-500 mb-3 items-center"
              v-html="leadHtml"
            />
            <!-- eslint-enable vue/no-v-html -->

            <AtomsAuthorSmallSection
              v-if="menuData?.author"
              class="mb-5"
              :author="menuData.author"
            />
          </div>
        </div>

        <div class="mb-10">
          <h2 class="text-brown-300 text-xl font-bold mb-3">
            この献立のレシピ（{{ menuData?.recipes.length }}品）
          </h2>

          <div class="grid grid-cols-1 md:grid-cols-2 gap-3 items-stretch mb-5">
            <NuxtLink
              v-for="recipe in menuData?.recipes"
              :key="recipe.id"
              :to="`/public/article/recipe/${recipe.old_identifier}`"
              class="flex gap-2 max-md:gap-4 hover:opacity-70 duration-300"
            >
              <div
                data-testid="recipe-image"
                class="w-28 h-28 max-md:w-2/5 max-md:h-auto max-md:aspect-[126/147] shrink-0"
              >
                <img
                  v-if="createRecipeImageUrl(recipe)"
                  :src="createRecipeImageUrl(recipe)"
                  class="rounded w-full h-full object-cover"
                />
                <AtomsPlaceholderImage
                  v-else
                  class="w-full h-full object-cover rounded bg-white"
                />
              </div>

              <div class="py-2">
                <div class="mb-1 md:flex items-baseline gap-2 max-md:mb-2">
                  <div class="flex items-baseline text-pink-400 max-md:mb-1">
                    <img
                      src="~/assets/img/fire.png"
                      class="mr-1 relative top-[3px] w-5"
                    />
                    <span class="font-bold">{{ recipe.energy }}</span>
                    kcal
                  </div>

                  <span
                    v-if="recipe.recipe_category_root"
                    :data-category-role="recipe.recipe_category_root.name_en"
                    class="font-bold"
                  >
                    {{ recipe.recipe_category_root.name }}
                  </span>
                </div>

                <h3 class="font-bold text-gray-500 text-lg">
                  {{ recipe.title }}
                </h3>
              </div>

              <div class="md:hidden flex items-center justify-end grow">
                <ChevronRight :size="30" class="text-brown-300" />
              </div>
            </NuxtLink>
          </div>
        </div>

        <AtomsContraindicationFoodSection
          v-if="menuData?.contraindication_food_groups"
          class="mb-10"
          :groups="menuData.contraindication_food_groups"
        />

        <AtomsAdPoets ad-spot-id="MzA3OjMwMjA" />

        <AtomsNutrientTable
          v-if="menuData?.nutrients"
          class="my-10"
          :nutrients="menuData.nutrients"
        />

        <div v-if="pointHtml" class="my-10">
          <h2 class="text-brown-300 text-xl font-bold mb-3">
            管理栄養士からのコツ・ポイント
          </h2>

          <!-- eslint-disable-next-line vue/no-v-html -->
          <div class="sanitized-html text-gray-500" v-html="pointHtml" />
        </div>

        <AtomsAuthorSection
          v-if="menuData?.author"
          class="mb-10"
          :author="menuData.author"
        />

        <AtomsShareSection />
      </div>
    </div>

    <div class="max-md:px-2">
      <AtomsAssociatedTagsSection
        v-if="menuData?.associated_tags"
        :tags="menuData.associated_tags"
        title="関連する献立の特徴から探す"
        class="mb-10"
        link-base-path="/public/article/menu"
      />

      <OrganismsMenuHistory class="my-10" />
      <OrganismsRecipeBottomAdsPc />
    </div>
  </div>
</template>

<style scoped>
@reference '~/assets/css/main.css';
:deep(.sanitized-html p:not(:last-child)) {
  @apply mb-2;
}

:deep(.sanitized-html a) {
  @apply underline;
}

:deep(.sanitized-html a[target='_blank']::after) {
  @apply font-dietplus-runes content-['\e92b'] inline-block mx-1 text-[#ad948b] text-[0.8rem];
}

[data-category-role='main'] {
  @apply text-pink-400;
}
[data-category-role='sub'] {
  @apply text-green-400;
}
[data-category-role='soup'] {
  @apply text-blue-500;
}
[data-category-role='dessert'] {
  @apply text-orange-500;
}
[data-category-role='staple'] {
  @apply text-cyan-400;
}
</style>
